import {
  Box,
  Button,
  ColumnDatas,
  DropdownBody,
  DropdownItem,
  getColor,
  Icons,
  Modal,
  Table,
  Tabs,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import { useCallback, useEffect, useRef, useState } from "react";
import "./index.scss";
import { useTranslation } from "react-i18next";
import utilsCommon from "../../shared/utils/common";
import AdminFilter from "../../components/AdminFilter";
import { FilterData } from "../../types/adminFilter";
import EmptyFilterPage from "../../components/EmptyPage/EmptyFilterPage";
import { Workgroup } from "../../types/workgroup";
import useAdminWorkgroupStore from "../../redux/dispatcher/useAdminWorkgroupStore";
import useCheckboxGroup from "../../shared/hooks/useCheckboxGroup";

import { nanoid } from "@reduxjs/toolkit";
import WorkgroupFileModal from "./WorkgroupFileModal";
import WorkgroupManagerModal from "./WorkgroupManagerModal";
import WorkgroupInfoModal from "../../components/WorkgroupModal";
import utilsDocs from "../../shared/utils/docs";
import WorkgroupAvatar from "../../components/WorkgroupAvatar";

const WorkgroupManage: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const {
    adminWorkgroupStore,
    getWorkgroupList,
    getDeletedWorkgroupList,
    changeWorkgroupStatus,
    getAdminWorkgroupDetail,
  } = useAdminWorkgroupStore();

  /**** State 모음 ****/
  const initFilterData = {
    workgroupMaster: "",
    workgroupName: "",
    // member: "",
    dateFilter: "all",
    date: {
      start: 0,
      end: 0,
    },
    order: "logDate",
    orderType: "DESC",
  } as FilterData;

  const [tab, setTab] = useState(0);
  const [filterData, setFilterData] = useState<FilterData>(initFilterData);
  const [deleteFilterData, setDeleteFilterData] =
    useState<FilterData>(initFilterData);
  const pageCount = 50;
  const [nextPage, setNextPage] = useState(-1);
  const [deleteNextPage, setDeleteNextPage] = useState(-1);
  const [total, setTotal] = useState(-1);
  const [deleteTotal, setDeleteTotal] = useState(-1);
  const [workgroupList, setWorkgroupList] = useState([] as Workgroup[]);
  const [deleteWorkgroupList, setDeleteWorkgroupList] = useState(
    [] as Workgroup[]
  );
  const workgroupCheckboxGroup = useCheckboxGroup<string>();
  const deleteWorkgroupCheckboxGroup = useCheckboxGroup<string>();
  const tableRef = useRef<HTMLDivElement>(null);
  const [initDone, setInitDone] = useState(false);
  const [workgroupMenu, setWorkgroupMenu] = useState<{
    x: number;
    y: number;
    target: Workgroup;
  }>({
    x: 0,
    y: 0,
    target: {} as Workgroup,
  });
  const [deleteWorkgroupMenu, setDeleteWorkgroupMenu] = useState<{
    x: number;
    y: number;
    target: Workgroup;
  }>({
    x: 0,
    y: 0,
    target: {} as Workgroup,
  });
  const inputRef = useRef<HTMLTableRowElement>(null);
  const [showConfirmDeleteModal, toggleConfirmDeleteModal] = useState(false);
  const [showConfirmDeleteForeverModal, toggleConfirmDeleteForeverModal] =
    useState(false);
  const [showConfirmRestoreModal, toggleConfirmRestoreModal] = useState(false);
  const [showWorkgroupDetailModal, toggleWorkgroupDetailModal] =
    useState(false);
  const [showWorkgroupFileModal, toggleWorkgroupFileModal] = useState(false);
  const [showWorkgroupManagerModal, toggleWorkgroupMangerModal] =
    useState(false);

  const [targetWorkgroupId, setTargetWorkgroupId] = useState("");
  const [targetWorkgroup, setTargetWorkgroup] = useState({} as Workgroup);

  /**** UseEffect 모음 ****/
  useEffect(() => {
    getWorkgroupListFunc();
    getDeletedWorkgroupListFunc();
    setInitDone(true);
  }, []);

  useEffect(() => {
    initDone && getWorkgroupListFunc();
  }, [filterData]);

  useEffect(() => {
    initDone && getDeletedWorkgroupListFunc();
  }, [deleteFilterData]);

  useEffect(() => {
    if (initDone) {
      if (tab === 0) {
        getWorkgroupListFunc();
      }
      if (tab === 1) {
        getDeletedWorkgroupListFunc();
      }
      workgroupCheckboxGroup.handleAllClick([]);
      deleteWorkgroupCheckboxGroup.handleAllClick([]);
    }
  }, [tab]);

  useEffect(() => {
    setWorkgroupList(adminWorkgroupStore.usingWorkgroup?.workgroupList ?? []);
    if (adminWorkgroupStore.usingWorkgroup.posPage === 1) {
      setTotal(adminWorkgroupStore.usingWorkgroup.total);
    }
    if (
      adminWorkgroupStore.usingWorkgroup.posPage * pageCount <
      adminWorkgroupStore.usingWorkgroup.total
    ) {
      setNextPage(adminWorkgroupStore.usingWorkgroup.posPage + 1);
    } else {
      setNextPage(-1);
    }
  }, [adminWorkgroupStore.usingWorkgroup]);

  useEffect(() => {
    if (adminWorkgroupStore.deleteWorkgroup.posPage === 1) {
      setDeleteWorkgroupList(adminWorkgroupStore.deleteWorkgroup.workgroupList);
      setDeleteTotal(adminWorkgroupStore.deleteWorkgroup.total);
    } else {
      setDeleteWorkgroupList(
        deleteWorkgroupList.concat(
          adminWorkgroupStore.deleteWorkgroup.workgroupList
        )
      );
    }
    if (
      adminWorkgroupStore.deleteWorkgroup.posPage * pageCount <
      adminWorkgroupStore.deleteWorkgroup.total
    ) {
      setDeleteNextPage(adminWorkgroupStore.deleteWorkgroup.posPage + 1);
    } else {
      setDeleteNextPage(-1);
    }
  }, [adminWorkgroupStore.deleteWorkgroup]);

  const handleTurnOffSettng = (): void => {
    setWorkgroupMenu({
      x: 0,
      y: 0,
      target: {} as Workgroup,
    });
    setDeleteWorkgroupMenu({
      x: 0,
      y: 0,
      target: {} as Workgroup,
    });
  };

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (inputRef.current && !(inputRef.current! as any).contains(e.target)) {
      handleTurnOffSettng();
      document.removeEventListener("click", handleClickOutside, true);
    }
  }, []);
  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  });

  const getWorkgroupListFunc = (page?: number) => {
    if (page !== -1) {
      getWorkgroupList({
        isDetail: "",
        orderBy: filterData.order + "",
        orderByType: filterData.orderType + "",
        status: 1,
        posPage: page ?? 1,
        pageRange: 1,
        rowRange: pageCount,
        searchDate: {
          start: filterData?.date?.start ?? 0,
          end: filterData?.date?.end ?? 0,
        },
        workgroupName: filterData.workgroupName + "",
        searchMemberIds: filterData.member
          ? [filterData.member]
          : ([] as string[]),
        managerId: filterData.workgroupMaster + "",
      });
    }
  };

  const getDeletedWorkgroupListFunc = (page?: number) => {
    if (page !== -1) {
      getDeletedWorkgroupList({
        isDetail: "",
        orderBy: deleteFilterData.order + "",
        orderByType: deleteFilterData.orderType + "",
        status: 0,
        posPage: page ?? 1,
        pageRange: 1,
        rowRange: pageCount,
        searchDate: {
          start: deleteFilterData?.date?.start ?? 0,
          end: deleteFilterData?.date?.end ?? 0,
        },
        workgroupName: deleteFilterData.workgroupName + "",
        searchMemberIds: deleteFilterData.member
          ? [deleteFilterData.member]
          : ([] as string[]),
        managerId: deleteFilterData.workgroupMaster + "",
      });
    }
  };

  const getWorkgroupDetailModal = async (target: Workgroup) => {
    const resp: any = await getAdminWorkgroupDetail(target.workgroupId);
    if (resp.payload?.workgroup) {
      setTargetWorkgroup(resp.payload.workgroup);
      toggleWorkgroupDetailModal(true);
    }
  };

  const getWorkgroupFileModal = async (target: Workgroup) => {
    setTargetWorkgroupId(target.workgroupId);
    toggleWorkgroupFileModal(true);
  };

  const getWorkgroupMangagerModal = async (target: Workgroup) => {
    const resp: any = await getAdminWorkgroupDetail(target.workgroupId);
    if (resp.payload?.workgroup) {
      setTargetWorkgroup(resp.payload.workgroup);
      toggleWorkgroupMangerModal(true);
    }
  };

  const changeStatusOneWorkgroup = (workgroupId: string, status: number) => {
    setTargetWorkgroupId(workgroupId);
    if (status === 0) {
      toggleConfirmDeleteModal(true);
    } else if (status === 1) {
      toggleConfirmRestoreModal(true);
    } else {
      toggleConfirmDeleteForeverModal(true);
    }
  };

  const renderWorkgroupMenuButton = (target: any) => {
    return (
      <DropdownBody
        dropdownRef={inputRef}
        width={190}
        location={{
          x: window.eco.serviceType === "OCI" ? target.x - 245 : target.x,
          y: target.y,
        }}
      >
        <DropdownItem
          label={t("admin.workgroup.details")}
          onClick={() => {
            getWorkgroupDetailModal(target.target);
            handleTurnOffSettng();
          }}
        />
        <DropdownItem
          label={t("admin.workgroup.viewAllFiles")}
          onClick={() => {
            getWorkgroupFileModal(target.target);
            handleTurnOffSettng();
          }}
        />
        <DropdownItem
          label={t("admin.workgroup.managerChange")}
          isHidden={target.target.workgroupType === "HUB"}
          onClick={() => {
            getWorkgroupMangagerModal(target.target);
            handleTurnOffSettng();
          }}
        />
        <DropdownItem
          label={t("admin.common.button.delete")}
          onClick={() => {
            changeStatusOneWorkgroup(target.target.workgroupId, 0);
            handleTurnOffSettng();
          }}
        />
      </DropdownBody>
    );
  };

  const renderDeleteWorkgroupMenuButton = (target: any) => {
    return (
      <DropdownBody
        dropdownRef={inputRef}
        width={190}
        location={{
          x: window.eco.serviceType === "OCI" ? target.x - 245 : target.x,
          y: target.y,
        }}
      >
        <DropdownItem
          label={t("admin.workgroup.details")}
          onClick={() => {
            getWorkgroupDetailModal(target.target);
            handleTurnOffSettng();
          }}
        />
        <DropdownItem
          label={t("admin.workgroup.viewAllFiles")}
          onClick={() => {
            getWorkgroupFileModal(target.target);
            handleTurnOffSettng();
          }}
        />
        {target.target.workgroupType === "GENERAL" && (
          <DropdownItem
            label={t("admin.workgroup.recovery")}
            onClick={() => {
              changeStatusOneWorkgroup(target.target.workgroupId, 1);
              handleTurnOffSettng();
            }}
          />
        )}
        <DropdownItem
          label={t("admin.workgroup.hardDelete")}
          onClick={() => {
            changeStatusOneWorkgroup(target.target.workgroupId, 2);
            handleTurnOffSettng();
          }}
        />
      </DropdownBody>
    );
  };

  const deleteModal = () => {
    const selectedGroup =
      targetWorkgroupId !== ""
        ? [targetWorkgroupId]
        : workgroupCheckboxGroup.selected;

    const isIncludeHub = workgroupList?.filter(
      (item) =>
        selectedGroup.includes(item.workgroupId) && item.workgroupType === "HUB"
    );
    const isIncludePrivate = workgroupList?.filter(
      (item) =>
        selectedGroup.includes(item.workgroupId) &&
        item.workgroupType === "PRIVATE"
    );
    return (
      <Modal
        width={540}
        title={
          isIncludeHub.length === 1 && selectedGroup.length === 1
            ? t("admin.workgroup.deletePrivateWorkgroupTitle")
            : t("admin.workgroup.deleteWorkgroup")
        }
        open={showConfirmDeleteModal}
        onClose={() => {
          toggleConfirmDeleteModal(false);
          setTargetWorkgroupId("");
        }}
        footer={
          <Box>
            <Button
              btnStyle="fill"
              label={t("admin.common.button.cancel")}
              type="secondary"
              onClick={() => {
                toggleConfirmDeleteModal(false);
                setTargetWorkgroupId("");
              }}
            />
            <Button
              btnStyle="fill"
              label={t("common.delete")}
              type={"error"}
              state={"hover"}
              onClick={async () => {
                let result: any = await changeWorkgroupStatus({
                  status: 0,
                  workgroupIds: selectedGroup,
                });

                if (result.error) {
                  toast.toastMsg(nanoid(), result.error.message, "error");
                } else {
                  toast.toastMsg(
                    nanoid(),
                    t("admin.common.alert.delete"),
                    "success"
                  );
                }

                getWorkgroupListFunc();
                workgroupCheckboxGroup.handleAllClick([]);
                setTargetWorkgroupId("");
                toggleConfirmDeleteModal(false);
              }}
            />
          </Box>
        }
        bodyMsg={
          isIncludeHub.length > 0
            ? t("admin.workgroup.deletePrivateWorkgroupsDesc")
            : isIncludePrivate.length > 0
            ? t("admin.workgroup.deleteOnlyPrivateWorkgroupsDesc")
            : t("admin.workgroup.deleteWorkgroupMsg")
        }
      />
    );
  };

  const changeStatus = (status: number) => {
    const tempTargetChangeWorkgroupIds =
      targetWorkgroupId !== ""
        ? [targetWorkgroupId]
        : deleteWorkgroupCheckboxGroup.selected;
    const isTargetSecretWorkgroupList = [
      ...workgroupList,
      ...deleteWorkgroupList,
    ].filter(
      (item) =>
        tempTargetChangeWorkgroupIds.includes(item.workgroupId) &&
        item.workgroupType !== "GENERAL"
    )?.length;
    return (
      <Modal
        width={540}
        title={
          status === 1
            ? t("admin.workgroup.workgroupRecovery")
            : t("admin.workgroup.workgroupHardDelete")
        }
        open={
          status === 1 ? showConfirmRestoreModal : showConfirmDeleteForeverModal
        }
        onClose={() => {
          status === 1
            ? toggleConfirmRestoreModal(false)
            : toggleConfirmDeleteForeverModal(false);
          setTargetWorkgroupId("");
        }}
        footer={
          <Box>
            <Button
              btnStyle="fill"
              label={t("admin.common.button.cancel")}
              type="secondary"
              onClick={() => {
                status === 1
                  ? toggleConfirmRestoreModal(false)
                  : toggleConfirmDeleteForeverModal(false);
                setTargetWorkgroupId("");
              }}
            />
            <Button
              btnStyle="fill"
              type={"primary"}
              label={
                status === 1
                  ? t("admin.workgroup.recovery")
                  : t("common.confirm")
              }
              onClick={async () => {
                let result: any = await changeWorkgroupStatus({
                  status: status,
                  workgroupIds: tempTargetChangeWorkgroupIds,
                });

                if (result.error) {
                  toast.toastMsg(nanoid(), result.error.message, "error");
                } else {
                  toast.toastMsg(
                    nanoid(),
                    status === 1
                      ? t("admin.common.alert.recover")
                      : t("admin.common.alert.hardDelete"),
                    "success"
                  );
                }

                getDeletedWorkgroupListFunc();
                deleteWorkgroupCheckboxGroup.handleAllClick([]);
                setTargetWorkgroupId("");
                status === 1
                  ? toggleConfirmRestoreModal(false)
                  : toggleConfirmDeleteForeverModal(false);
              }}
            />
          </Box>
        }
        bodyMsg={
          status !== 1
            ? t("admin.workgroup.workgroupHardDeleteMsg")
            : isTargetSecretWorkgroupList
            ? t("admin.workgroup.workgroupRecoveryPrivate")
            : t("admin.workgroup.workgroupRecoveryMsg")
        }
      />
    );
  };

  function transformFileList(data: any[] | undefined, tab: number) {
    const dataTransformed = [] as ColumnDatas[];
    data &&
      data?.map((item: any, index: number) => {
        if (item && item?.workgroupId && item?.workgroupName) {
          const dataItemTransformed = {
            data: [
              {
                columnText: (
                  <Tooltip message={item.workgroupName}>
                    <Box alignItems="center">
                      <WorkgroupAvatar item={item} />
                      <Box classes="mg ml-8" maxWidth={"calc(100% - 40px)"}>
                        {item.workgroupType !== "GENERAL" && (
                          <Box minWidth={20}>
                            <Icons variant="fill" label="lock" size={20} />
                          </Box>
                        )}
                        <Typography
                          classes="mg ml-4"
                          ellipsis
                          maxWidth={"100%"}
                        >
                          {item.workgroupName}
                        </Typography>
                      </Box>
                    </Box>
                  </Tooltip>
                ),
              },
              {
                columnText: (
                  <Tooltip
                    message={utilsCommon.getNameWithId(
                      item.managerName,
                      item.managerId
                    )}
                  >
                    {utilsCommon.getNameWithId(
                      item.managerName,
                      item.managerId
                    )}
                  </Tooltip>
                ),
              },
              {
                columnText: item.internalUserCount + item.externalUserCount,
              },
              {
                columnText: item.docCount,
              },
              {
                columnText: utilsDocs.fileSizeFormat(item.docSize),
              },
              {
                columnText:
                  item.endDate === -1
                    ? t("admin.common.unlimited")
                    : utilsCommon.renderDateFormat(item.endDate),
              },
              {
                columnText: utilsCommon.renderDateFormat(item.logDate),
              },
            ],
            id: item.workgroupId,
            checkBoxClick: () => {
              tab === 0 && workgroupCheckboxGroup.handleClick(item.workgroupId);
              tab === 1 &&
                deleteWorkgroupCheckboxGroup.handleClick(item.workgroupId);
            },
            hover: (
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  tab === 0 &&
                    setWorkgroupMenu({
                      x:
                        window.eco.serviceType === "OCI"
                          ? e.pageX - 240
                          : e.pageX,
                      y:
                        window.eco.serviceType === "OCI"
                          ? e.pageY - 61
                          : e.pageY,
                      target: item,
                    });
                  tab === 1 &&
                    setDeleteWorkgroupMenu({
                      x:
                        window.eco.serviceType === "OCI"
                          ? e.pageX - 240
                          : e.pageX,
                      y:
                        window.eco.serviceType === "OCI"
                          ? e.pageY - 61
                          : e.pageY,
                      target: item,
                    });
                }}
                btnStyle="icon"
                type="primary"
                size="sm"
                leadingIcon={<Icons variant="line" label="moreVertical" />}
              />
            ),
          };
          dataTransformed.push(dataItemTransformed);
        }
      });
    return dataTransformed;
  }

  return (
    <Box classes="adminWorkgroup" direction="column">
      <Box classes="adminWorkgroup-title" justifyContent="space-between">
        <Typography type="h1">{t("admin.menu.workgroup")}</Typography>
      </Box>
      <Box classes="adminWorkgroup-title" justifyContent="space-between">
        {tab === 0 ? (
          <AdminFilter
            initFilterData={initFilterData}
            filterData={filterData}
            setFilterData={setFilterData}
          />
        ) : (
          <AdminFilter
            initFilterData={initFilterData}
            filterData={deleteFilterData}
            setFilterData={setDeleteFilterData}
          />
        )}
        {tab === 0 ? (
          <Box>
            <Button
              btnStyle="fill"
              type="error"
              onClick={() => toggleConfirmDeleteModal(true)}
              label={t("admin.common.button.delete")}
              state={
                workgroupCheckboxGroup.selected.length > 0
                  ? "default"
                  : "disable"
              }
            />
          </Box>
        ) : (
          <Box>
            <Button
              btnStyle="fill"
              type="error"
              onClick={() => toggleConfirmDeleteForeverModal(true)}
              label={t("admin.workgroup.hardDelete")}
              state={
                deleteWorkgroupCheckboxGroup.selected.length > 0
                  ? "default"
                  : "disable"
              }
            />
            <Button
              classes="mg ml-8"
              btnStyle="fill"
              type="primary"
              onClick={() => toggleConfirmRestoreModal(true)}
              label={t("admin.workgroup.recovery")}
              state={
                deleteWorkgroupCheckboxGroup.selected.length > 0
                  ? "default"
                  : "disable"
              }
            />
          </Box>
        )}
      </Box>
      <Tabs
        tabs={[
          {
            color: "default",
            isNew: false,
            text: (
              <Box>
                <Typography type="subtitle2">
                  {t("admin.workgroup.activeWorkgroup")}
                </Typography>
                {total > -1 && (
                  <Typography
                    type="subtitle2"
                    classes="mg ml-8"
                    exactColor={getColor("COLOR_TEXT_BRAND")}
                  >
                    {total}
                  </Typography>
                )}
              </Box>
            ),
          },
          {
            color: "default",
            isNew: false,
            text: (
              <Box>
                <Typography type="subtitle2">
                  {t("admin.workgroup.deActiveWorkgroup")}
                </Typography>
                {deleteTotal > -1 && (
                  <Typography
                    classes="mg ml-8"
                    type="subtitle2"
                    exactColor={getColor("COLOR_TEXT_BRAND")}
                  >
                    {deleteTotal}
                  </Typography>
                )}
              </Box>
            ),
          },
        ]}
        type={"leftFull"}
        selectTab={(value) => {
          setTab(value);
        }}
        selected={tab}
        size={"md"}
      />
      <Box width={"100%"}>
        {tab === 0 && (
          <Box
            ref={tableRef}
            classes="mg mt-12"
            width={"100%"}
            direction="column"
          >
            <Table
              width={"100%"}
              height={
                workgroupList?.length === 0 ? "0px" : "calc(100vh - 330px)"
              }
              header={[
                {
                  headerText: t("admin.workgroup.workgroupName"),
                  width: "calc(100% - 992px)",
                  order:
                    filterData.order === "workgroupName"
                      ? filterData.orderType === "ASC"
                        ? "upOn"
                        : "downOn"
                      : "downOff",
                  orderAction: () => {
                    filterData.order === "workgroupName"
                      ? filterData.orderType === "ASC"
                        ? setFilterData({
                            ...filterData,
                            orderType: "DESC",
                          })
                        : setFilterData({
                            ...filterData,
                            orderType: "ASC",
                          })
                      : setFilterData({
                          ...filterData,
                          order: "workgroupName",
                          orderType: "ASC",
                        });
                  },
                },
                {
                  headerText: t("admin.workgroup.manager"),
                  width: "300px",
                  order:
                    filterData.order === "managerId"
                      ? filterData.orderType === "ASC"
                        ? "upOn"
                        : "downOn"
                      : "downOff",
                  orderAction: () => {
                    filterData.order === "managerId"
                      ? filterData.orderType === "ASC"
                        ? setFilterData({
                            ...filterData,
                            orderType: "DESC",
                          })
                        : setFilterData({
                            ...filterData,
                            orderType: "ASC",
                          })
                      : setFilterData({
                          ...filterData,
                          order: "managerId",
                          orderType: "ASC",
                        });
                  },
                },
                {
                  headerText: t("admin.workgroup.member"),
                  width: "150px",
                  order: "",
                  orderAction: () => {},
                },
                {
                  headerText: t("admin.workgroup.sharedFiles"),
                  width: "150px",
                  order: "",
                  orderAction: () => {},
                },
                {
                  headerText: t("admin.workgroup.storageUse"),
                  width: "150px",
                  order: "",
                  orderAction: () => {},
                },
                {
                  headerText: t("admin.workgroup.workgroupPeriod"),
                  width: "200px",
                  order:
                    filterData.order === "endDate"
                      ? filterData.orderType === "ASC"
                        ? "upOn"
                        : "downOn"
                      : "downOff",
                  orderAction: () => {
                    filterData.order === "endDate"
                      ? filterData.orderType === "ASC"
                        ? setFilterData({
                            ...filterData,
                            orderType: "DESC",
                          })
                        : setFilterData({
                            ...filterData,
                            orderType: "ASC",
                          })
                      : setFilterData({
                          ...filterData,
                          order: "endDate",
                          orderType: "ASC",
                        });
                  },
                },
                {
                  headerText: t("admin.workgroup.creationDate"),
                  width: "200px",
                  order:
                    filterData.order === "logDate"
                      ? filterData.orderType === "ASC"
                        ? "upOn"
                        : "downOn"
                      : "downOff",
                  orderAction: () => {
                    filterData.order === "logDate"
                      ? filterData.orderType === "ASC"
                        ? setFilterData({
                            ...filterData,
                            orderType: "DESC",
                          })
                        : setFilterData({
                            ...filterData,
                            orderType: "ASC",
                          })
                      : setFilterData({
                          ...filterData,
                          order: "logDate",
                          orderType: "ASC",
                        });
                  },
                },
              ]}
              data={transformFileList(workgroupList, 0)}
              totalCount={workgroupList?.length}
              clickIds={workgroupCheckboxGroup.selected}
              checkBoxClick={() =>
                workgroupCheckboxGroup.handleAllClick(
                  workgroupCheckboxGroup.selected.length ===
                    workgroupList.length
                    ? []
                    : workgroupList.map((q) => q.workgroupId + "")
                )
              }
              endReached={() => {
                getWorkgroupListFunc(nextPage);
              }}
              columnHeight="md"
            />
            {workgroupList.length === 0 && <EmptyFilterPage />}
          </Box>
        )}
        {tab === 1 && (
          <Box
            ref={tableRef}
            width={"100%"}
            classes="mg mt-12"
            direction="column"
          >
            <Table
              width={"100%"}
              height={
                deleteWorkgroupList.length === 0 ? "0px" : "calc(100vh - 330px)"
              }
              header={[
                {
                  headerText: t("admin.workgroup.workgroupName"),
                  width: "calc(100% - 992px)",
                  order:
                    deleteFilterData.order === "workgroupName"
                      ? deleteFilterData.orderType === "ASC"
                        ? "upOn"
                        : "downOn"
                      : "downOff",
                  orderAction: () => {
                    deleteFilterData.order === "workgroupName"
                      ? deleteFilterData.orderType === "ASC"
                        ? setDeleteFilterData({
                            ...deleteFilterData,
                            orderType: "DESC",
                          })
                        : setDeleteFilterData({
                            ...deleteFilterData,
                            orderType: "ASC",
                          })
                      : setDeleteFilterData({
                          ...deleteFilterData,
                          order: "workgroupName",
                          orderType: "ASC",
                        });
                  },
                },
                {
                  headerText: t("admin.workgroup.manager"),
                  width: "300px",
                  order:
                    deleteFilterData.order === "managerId"
                      ? deleteFilterData.orderType === "ASC"
                        ? "upOn"
                        : "downOn"
                      : "downOff",
                  orderAction: () => {
                    deleteFilterData.order === "managerId"
                      ? deleteFilterData.orderType === "ASC"
                        ? setDeleteFilterData({
                            ...deleteFilterData,
                            orderType: "DESC",
                          })
                        : setDeleteFilterData({
                            ...deleteFilterData,
                            orderType: "ASC",
                          })
                      : setDeleteFilterData({
                          ...deleteFilterData,
                          order: "managerId",
                          orderType: "ASC",
                        });
                  },
                },
                {
                  headerText: t("admin.workgroup.member"),
                  width: "150px",
                  order: "",
                  orderAction: () => {},
                },
                {
                  headerText: t("admin.workgroup.sharedFiles"),
                  width: "150px",
                  order: "",
                  orderAction: () => {},
                },
                {
                  headerText: t("admin.workgroup.storageUse"),
                  width: "150px",
                  order: "",
                  orderAction: () => {},
                },
                {
                  headerText: t("admin.workgroup.workgroupPeriod"),
                  width: "200px",
                  order:
                    deleteFilterData.order === "endDate"
                      ? deleteFilterData.orderType === "ASC"
                        ? "upOn"
                        : "downOn"
                      : "downOff",
                  orderAction: () => {
                    deleteFilterData.order === "endDate"
                      ? deleteFilterData.orderType === "ASC"
                        ? setDeleteFilterData({
                            ...deleteFilterData,
                            orderType: "DESC",
                          })
                        : setDeleteFilterData({
                            ...deleteFilterData,
                            orderType: "ASC",
                          })
                      : setDeleteFilterData({
                          ...deleteFilterData,
                          order: "endDate",
                          orderType: "ASC",
                        });
                  },
                },
                {
                  headerText: t("admin.workgroup.creationDate"),
                  width: "200px",
                  order:
                    filterData.order === "logDate"
                      ? deleteFilterData.orderType === "ASC"
                        ? "upOn"
                        : "downOn"
                      : "downOff",
                  orderAction: () => {
                    deleteFilterData.order === "logDate"
                      ? deleteFilterData.orderType === "ASC"
                        ? setDeleteFilterData({
                            ...deleteFilterData,
                            orderType: "DESC",
                          })
                        : setDeleteFilterData({
                            ...deleteFilterData,
                            orderType: "ASC",
                          })
                      : setDeleteFilterData({
                          ...deleteFilterData,
                          order: "logDate",
                          orderType: "ASC",
                        });
                  },
                },
              ]}
              data={transformFileList(deleteWorkgroupList, 1)}
              totalCount={deleteWorkgroupList.length}
              clickIds={deleteWorkgroupCheckboxGroup.selected}
              checkBoxClick={() =>
                deleteWorkgroupCheckboxGroup.handleAllClick(
                  deleteWorkgroupCheckboxGroup.selected.length ===
                    deleteWorkgroupList.length
                    ? []
                    : deleteWorkgroupList.map((q) => q.workgroupId + "")
                )
              }
              endReached={() => {
                getDeletedWorkgroupListFunc(deleteNextPage);
              }}
              columnHeight="md"
            />
            {deleteWorkgroupList.length === 0 && <EmptyFilterPage />}
          </Box>
        )}
        {workgroupMenu.x > 0 &&
          workgroupMenu.y > 0 &&
          renderWorkgroupMenuButton(workgroupMenu)}
        {deleteWorkgroupMenu.x > 0 &&
          deleteWorkgroupMenu.y > 0 &&
          renderDeleteWorkgroupMenuButton(deleteWorkgroupMenu)}
      </Box>
      {showConfirmDeleteModal && deleteModal()}
      {showConfirmRestoreModal && changeStatus(1)}
      {showConfirmDeleteForeverModal && changeStatus(2)}
      {showWorkgroupDetailModal && targetWorkgroup.workgroupId && (
        <WorkgroupInfoModal
          open={showWorkgroupDetailModal}
          isWorkgroupManager={false}
          workgroupInfo={targetWorkgroup}
          fromAdmin
          onClose={() => {
            toggleWorkgroupDetailModal(false);
            setTargetWorkgroupId("");
            setTargetWorkgroup({} as Workgroup);
          }}
        />
      )}
      {showWorkgroupFileModal && targetWorkgroupId !== "" && (
        <WorkgroupFileModal
          workgroupId={targetWorkgroupId}
          showWorkgroupFilelModal={showWorkgroupFileModal}
          onClose={() => {
            toggleWorkgroupFileModal(false);
            setTargetWorkgroupId("");
            setTargetWorkgroup({} as Workgroup);
          }}
        />
      )}
      {showWorkgroupManagerModal && targetWorkgroup.workgroupId && (
        <WorkgroupManagerModal
          workgroupInfo={targetWorkgroup}
          showWorkgroupManagerModal={showWorkgroupManagerModal}
          onClose={() => {
            toggleWorkgroupMangerModal(false);
            setTargetWorkgroupId("");
            setTargetWorkgroup({} as Workgroup);
          }}
        />
      )}
    </Box>
  );
};

export default WorkgroupManage;
